/**
 * Função de cada unidade (GDD › Unidades), mostrada na ajuda ⓘ. Os números (ATQ, DEF, custo…)
 * vêm de `units.ts`; aqui só o papel da unidade e as regras especiais.
 */
export const UNIT_DESCRIPTIONS: Record<string, string> = {
  // Exército
  soldier:
    'Infantaria básica. Única unidade que conquista território terrestre (se sobrar depois do ataque). Todo território precisa manter pelo menos 1 soldado. Come 1 ALI por turno.',
  truck: 'Leva 10 soldados, que passam a ter movimento 2. Não combate.',
  artillery: 'Forte no ataque e fraca na defesa. Em Apoio, sofre só 1/4 das perdas.',
  'recon-armored':
    'Reconhecimento em tempo real de um território terrestre vizinho: revela tropas terrestres e instalações. Tem 30% de chance de ser detectado e destruído.',
  tank: 'Blindado de ataque. Pode ficar em território inimigo depois de vencer, sem conquistar (território contestado).',
  engineers:
    'Cada 5 engenheiros num ataque anulam 1 nível de Fortificações do alvo. Permitem construir Infraestrutura e Industrial em território ocupado.',
  'mobile-aa':
    'Antiaéreo: 3× contra aviões e helicópteros, não atinge o resto. Soma +3 à cobertura antiaérea do território.',
  'mobile-missile-launcher':
    'Sai do Quartel já carregado com um míssil de cruzeiro (poder 30, alcance 3). Combate como artilharia até disparar; o lançamento consome a unidade.',
  // Aeronáutica
  'recon-plane-prop':
    'Espiona o destino (tropas e instalações) e, em faixas, os territórios sobrevoados. Facilmente detectado pelo antiaéreo.',
  'prop-fighter':
    'Caça: abate aviões e helicópteros inimigos, escolta bombardeiros, reforça a defesa antiaérea onde está baseado e dá Apoio aéreo a combates terrestres (0,5× contra unidades terrestres).',
  'prop-bomber':
    'Bombardeiro: destrói níveis de uma instalação escolhida, parte do estoque ou mata tropas (alvo Tropas: ΣPB ÷ 2 soldados equivalentes, menos 10% por nível de Fortificações). Não causa dano em combate. Facilmente detectado.',
  'recon-plane-twin': 'Reconhecimento de maior alcance (4). Espiona o destino e, em faixas, o caminho.',
  'twin-bomber': 'Bombardeiro de alcance 3 e poder de bombardeio 10. Facilmente detectado.',
  'heavy-bomber':
    'Bombardeiro de alcance 4 e poder de bombardeio 20. Pode levar ogiva nuclear. Facilmente detectado.',
  'cargo-plane': 'Transporta 100 recursos de aeroporto a aeroporto (rota logística aérea).',
  'transport-plane':
    'Transporta 10 soldados ou 2 veículos entre aeroportos ou bases aéreas próprios ou aliados e fica no destino com a carga. Parte de Aeroporto ou Base aérea.',
  'jet-fighter':
    'Caça a jato: mais forte e de maior alcance que o caça a hélice. Mesmo papel (superioridade aérea, escolta, apoio).',
  'jet-recon-plane':
    'Reconhecimento a jato: medianamente detectado e permite rota sem volta (alcance dobrado).',
  'jet-bomber':
    'Bombardeiro a jato: poder de bombardeio 30, medianamente detectado. Pode levar ogiva nuclear.',
  'stealth-fighter':
    'Caça stealth: escolta ideal do bombardeiro stealth, nas mesmas condições de alcance e detecção.',
  'stealth-bomber':
    'Bombardeiro stealth: dificilmente detectado no destino e invisível no caminho. Pode levar ogiva nuclear.',
  'transport-helicopter':
    'Move-se com Marcha (movimento 3, mais rápido que o caminhão) levando 10 tropas ou 1 veículo. Carga de saque 30.',
  'recon-helicopter': 'Reconhecimento em tempo real com alcance 2, medianamente detectado.',
  'attack-helicopter': 'Combate terrestre com Marcha (movimento 2); 2× contra blindados.',
  // Aviação naval
  'carrier-fighter':
    'Caça embarcado: opera de porta-aviões ou Base aérea costeira. Com Propulsão a jato ganha +50% de ATQ e DEF e alcance 3.',
  'torpedo-bomber':
    'Torpedeiro embarcado: Ataque naval (2,5× contra navios) e bombardeio leve em terra (poder 5), inclusive a partir do porta-aviões.',
  'asw-helicopter':
    'Sempre detecta submarinos (3× contra eles). Embarca em fragata (1), cruzador (2) ou porta-aviões.',
  // Marinha
  'merchant-ship': 'Transporta recursos entre portos (rota logística marítima). Não tem armas.',
  corvette:
    'Patrulha pequena e rápida. Detecta navios de superfície inimigos nas zonas vizinhas a cada turno. Não controla zona.',
  destroyer:
    'Contratorpedeiro: navio de linha (controla a zona), rápido e bom contra submarinos (sempre os detecta; 1,5× contra eles).',
  'landing-ship':
    'Transporta 10 tropas e 3 veículos (ou 100 recursos) e permite o desembarque anfíbio: as tropas embarcam junto numa Marcha e, no turno seguinte, desembarcam com Marcha da zona para a costa (−30% de ATQ).',
  submarine:
    'Furtivo: só é atingido se detectado. Em Defesa, ataca navios inimigos que entram ou passam pela zona; intercepta comboios. Não controla zona.',
  'recon-submarine':
    'Espiona as frotas das zonas por onde passa, inclusive submarinos detectados. 20% de chance de ser detectado (sempre por contratorpedeiros e fragatas).',
  frigate:
    'Navio de linha especializado em detectar submarinos (2,5× contra eles) e proteger outros navios; 1× contra aeronaves.',
  cruiser:
    'Navio de linha equilibrado: ataca embarcações e dá Apoio a territórios costeiros. Com Mísseis navais, embarca 4 mísseis de cruzeiro (Frota) e os lança.',
  battleship:
    'Navio de linha muito resistente e lento, com canhões fortíssimos. Apoio à costa com 1,5× contra unidades terrestres.',
  'aircraft-carrier':
    'Aeroporto móvel para a aviação naval (12 aeronaves, 3 missões por turno). Não controla zona sozinho.',
  'nuclear-submarine':
    'Submarino de ataque que não gasta combustível. Embarca 4 mísseis de cruzeiro (Frota) e os lança (alcance 4).',
  ssbn: 'Embarca 4 SLBM e até 2 ogivas nucleares (Frota): a garantia de retaliação nuclear.',
  // Inteligência
  agent:
    'Missões de espionagem em tempo real a partir da Agência de inteligência: infiltração, interceptar ordens, sabotagem, fomentar revolta e roubo de tecnologia. Estacionado num território, reforça a contraespionagem.',
  // Mísseis
  'cruise-missile':
    'Consumível. Age como um bombardeio sem volta (poder 30), alcance 4. Produzido na Base naval, embarcado (Frota) e lançado de cruzador ou submarino nuclear. Interceptável por antiaéreo e Defesa antimíssil.',
  mrbm: 'Míssil balístico de médio alcance (8), poder 60. Só a Defesa antimíssil intercepta. Lançado do Silo; pode levar ogiva nuclear.',
  slbm: 'Míssil balístico embarcado (Frota) e lançado de SSBN (alcance 8, poder 60). Pode levar ogiva nuclear.',
  icbm: 'Míssil balístico intercontinental (alcance ilimitado, poder 90). Lançado do Silo; pode levar ogiva nuclear.',
  'nuclear-warhead':
    'Ogiva acoplada a míssil balístico, SLBM ou ICBM, ou levada por bombardeiro quadrimotor, a jato ou stealth. Mata 80% das unidades, tira 3 níveis de todas as instalações e contamina o território por 5 turnos.',
  'asat-missile': 'Destrói um satélite inimigo com 70% de chance. Lançado da Agência espacial.',
  // Espaço
  'inclined-satellite':
    'Reconhecimento de uma faixa de latitude (Hemisfério Norte, Equatorial ou Hemisfério Sul, escolhida no lançamento) com dados do turno anterior, por 40 turnos. Não revela ordens, estoques nem submarinos.',
  'polar-satellite':
    'Reconhecimento global com dados do turno anterior, por 40 turnos. Não revela ordens, estoques nem submarinos.',
  'geo-satellite':
    'Visão ao vivo de uma região (continente ou oceano) por 40 turnos. Não revela ordens, estoques nem submarinos.',
  // Neutros
  militia: 'Defensores de territórios neutros. Não são produzidos.',
};
